import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Button, FormControl, Grid, InputLabel, Link, OutlinedInput, Select } from "@mui/material";
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from 'react-intl';
import CustomDialog from '../components/CustomDialog';
import FlagIcon from '../components/FlagIcon';
import { useLanguage } from "../hooks/useLanguage";
import { getDefaultLanguage, LOCALE_KEYS } from "../utils/lib";
import AttributeRegionSelectionValue from './AttributeRegionSelectionValue';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export default function AttributeBaseSelection({ updateDefaults, updateValidation, validation, defaults }) {

  const intl = useIntl();

  const [selectedLanguages, setSelectedLanguages] = useState(validation['BASE']?.values?.length ? Object.keys(validation['BASE'].values[0]).filter(k => k !== 'id') : []);

  const { locale } = useLanguage();

  const [isOpen, setIsOpen] = useState({ state: false, nextId: null, tocall: null, initialValues: null });

  // all selection-values
  const values = useMemo(() => {
    if (validation['BASE']?.values) {
      return validation['BASE'].values;
    } else {
      return [];
    }
  }, [validation['BASE']]);

  // set current locale as the default language in the selectedLanguages-array
  useEffect(() => {
    setSelectedLanguages(old => {
      if (old.length === 0) {
        return [locale];
      } else {
        return old;
      }
    })
  }, [locale]);

  /**
   * Should be called when a selection-value is either edited or newly created.
   * @param {*} type either 'create' or 'edit'
   * @param {*} initialValues the initial-language-values when type === 'edit'
   */
  const handleOpenDialog = (type, initialValues) => {
    if (type === 'create') {
      setIsOpen(() => {
        const newId = crypto.randomUUID();
        return { state: true, nextId: newId, tocall: addValueToSelection };
      })
    } else if (type === 'edit') {
      setIsOpen(() => {
        return { state: true, nextId: initialValues.id, tocall: updateSelectionValue(initialValues.id), initialValues };
      })
    }
  }

  /**
   * Should be called for closing the dialog.
   */
  const handleCloseDialog = () => {
    setIsOpen({ state: false, nextId: null });
  }

  /**
   * Handles a change in selected languages.
   * At least one language must be selected.
   * Sets the value 'Default value' for all new languages.
   * @param {*} event 
   * @returns 
   */
  const handleChangeLanguages = (event) => {
    const { target: { value } } = event;
    // do not allow an empty array to be set
    if (!value || !value.length) return;
    // update all languages in existing selection-values
    let oldLangs = [...selectedLanguages];
    let newLangs = [];
    newLangs = value.filter(lang => !oldLangs.includes(lang));
    oldLangs = oldLangs.filter(lang => !value.includes(lang));
    updateValidation({
      ...validation,
      'BASE': {
        values: values.map(languageObj => {
          let obj = { ...languageObj };
          oldLangs.forEach(lang => delete obj[lang]);
          newLangs.forEach(lang => obj[lang] = "Default value");
          return obj;
        })
      }
    });
    setSelectedLanguages(value);
  };

  /**
   * Adds a value to the selection and closes the dialog.
   * @param {*} newValue 
   */
  const addValueToSelection = (newValue) => {
    // add newValue to every region by default
    let newValidation = { ...validation };
    if (!newValidation['BASE']) {
      newValidation['BASE'] = {};
    }
    // add newValue
    newValidation['BASE'] = { ...newValidation['BASE'], values: [...(newValidation['BASE']?.values ?? []), newValue] };
    updateValidation(newValidation);
    handleCloseDialog();
  };

  /**
   * Removes the specified value from the selection (by id) and resets the default-value (if the specified value is the default value).
   * @param {*} value 
   */
  const removeValueFromSelection = (value) => {
    let newDefaults = { ...defaults };
    for (let region in newDefaults) {
      if (defaults[region]?.value === value.id) {
        // reset the default value
        newDefaults[region].value = null;
      }
    }
    updateDefaults(newDefaults);
    let newValidation = { ...validation };
    for (let region in validation) {
      if (region === 'BASE') {
        // filter id of values
        newValidation[region] = { ...newValidation[region], values: [...newValidation[region].values.filter(v => v.id !== value.id)] };
      } else {
        // filter values direct
        newValidation[region] = { ...(newValidation[region] ?? {}), values: [...(newValidation[region]?.values ?? []).filter(v => v !== value.id)] };
      }
    }
    updateValidation(newValidation);
  };

  /**
   * Updates the selection-value with the specified id.
   * @param {*} id 
   * @returns 
   */
  const updateSelectionValue = (id) => (newValue) => {
    updateValidation({
      ...validation,
      'BASE': {
        values: values.map(value => {
          if (value.id === id) {
            return { ...newValue, id }
          } else {
            return value;
          }
        })
      }
    });
    handleCloseDialog();
  };

  return (
    <Grid container item xs={12} spacing={1} >
      <Grid item xs={12} sm={6} md={3}>
        <FormControl sx={{ m: 0, width: '90%' }}>
          <InputLabel id="demo-multiple-checkbox-label"><FormattedMessage id="EditAttribute.selection" /></InputLabel>
          <Select
            labelId="demo-multiple-checkbox-label"
            id="demo-multiple-checkbox"
            multiple
            value={selectedLanguages}
            onChange={handleChangeLanguages}
            input={<OutlinedInput label={intl.formatMessage({ id: "EditAttribute.selection" })} />}
            renderValue={(selected) => selected.join(', ')}
            MenuProps={MenuProps}
          >
            {LOCALE_KEYS.map((key) => {
              const selected = selectedLanguages.includes(key);
              const SelectionIcon = selected ? CheckBoxIcon : CheckBoxOutlineBlankIcon;

              return (
                <MenuItem key={key} value={key}>
                  <SelectionIcon
                    fontSize="small"
                    style={{ marginRight: 8, padding: 9, boxSizing: 'content-box' }}
                  />
                  <FlagIcon code={key} />
                  <ListItemText primary={key} />
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} >
        <Button variant="contained" onClick={() => handleOpenDialog('create')} ><FormattedMessage id="EditAttribute.addValue" /></Button>
      </Grid>

      <Grid item container xs={12} sm={6} md={3} spacing={1}>
        {
          values.map((v, i) => {
            return (
              <Grid container key={i} item xs={12} sx={{
                justifyContent: "space-between",
                alignItems: "center",
                flexDirection: "row",
                gap: 3
              }} >
                <div>
                  {getDefaultLanguage(v, locale)}
                </div>
                <div>
                  <Button
                    component={Link}
                    color="primary"
                    aria-label="edit"
                    onClick={() => handleOpenDialog('edit', v)}
                  >
                    <EditIcon />
                  </Button>
                  <Button
                    component={Link}
                    color="error"
                    aria-label="edit"
                    onClick={() => removeValueFromSelection(v)}
                  >
                    <DeleteIcon />
                  </Button>
                </div>
              </Grid>
            )
          })
        }
      </Grid>
      <CustomDialog
        isOpen={isOpen.state}
        handleClose={handleCloseDialog}
        title={intl.formatMessage({ id: "EditAttribute.addValue" })}
        content={<AttributeRegionSelectionValue selectedLanguages={selectedLanguages} addValue={isOpen.tocall} id={isOpen.nextId} initialValues={isOpen.initialValues} />}
        maxWidth="lg"
        height='50vh'
        hideConfirmation={true}
        displayClose={true}
      />
    </Grid>
  )
}